/**
 * ProviderProfile
 * Edit provider business information, contact, service areas, hours, and social links.
 */
import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api';

const SECTORS = [
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'telecom', label: 'Telecom' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'finance', label: 'Finance' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'home-services', label: 'Home Services' },
  { value: 'retail', label: 'Retail' },
  { value: 'travel', label: 'Travel' },
  { value: 'legal', label: 'Legal' },
  { value: 'education', label: 'Education' },
];

const PROVINCES = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_HOURS = DAYS.reduce((acc, day) => {
  acc[day] = { open: '09:00', close: '17:00', closed: day === 'Saturday' || day === 'Sunday' };
  return acc;
}, {});

const DEFAULT_PROFILE = {
  name: '',
  description: '',
  logoUrl: '',
  sector: 'healthcare',
  province: 'ON',
  city: '',
  phone: '',
  email: '',
  website: '',
  serviceAreas: ['ON'],
  businessHours: DEFAULT_HOURS,
  socialLinks: { linkedin: '', twitter: '', facebook: '', instagram: '' },
};

export default function ProviderProfile() {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState(null);

  // Fetch profile
  const { data: profileRaw } = useQuery({
    queryKey: ['provider-profile'],
    queryFn: () => api.get('/api/provider-portal/profile').catch(() => null),
    placeholderData: null,
  });

  const initial = profileRaw || DEFAULT_PROFILE;
  const [form, setForm] = useState({ ...DEFAULT_PROFILE, ...initial });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data) => api.put('/api/provider-portal/profile', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-profile'] });
      setToast('Profile saved successfully.');
      setTimeout(() => setToast(null), 4000);
    },
    onError: (err) => {
      setToast(`Error: ${err.message}`);
      setTimeout(() => setToast(null), 5000);
    },
  });

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleServiceAreaToggle = useCallback((prov) => {
    setForm((prev) => {
      const areas = prev.serviceAreas.includes(prov)
        ? prev.serviceAreas.filter((a) => a !== prov)
        : [...prev.serviceAreas, prov];
      return { ...prev, serviceAreas: areas };
    });
  }, []);

  const handleHoursChange = useCallback((day, field, value) => {
    setForm((prev) => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: { ...prev.businessHours[day], [field]: value },
      },
    }));
  }, []);

  const handleHoursClosedToggle = useCallback((day) => {
    setForm((prev) => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: { ...prev.businessHours[day], closed: !prev.businessHours[day].closed },
      },
    }));
  }, []);

  const handleSocialChange = useCallback((platform, value) => {
    setForm((prev) => ({
      ...prev,
      socialLinks: { ...prev.socialLinks, [platform]: value },
    }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    saveMutation.mutate(form);
  }, [form, saveMutation]);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
          Provider Profile
        </h1>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          Update your business information visible to potential customers.
        </p>
      </div>

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed', top: '24px', right: '24px', zIndex: 1000,
          padding: '12px 20px', borderRadius: '10px',
          backgroundColor: toast.startsWith('Error') ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${toast.startsWith('Error') ? '#fecaca' : '#86efac'}`,
          color: toast.startsWith('Error') ? '#991b1b' : '#166534',
          fontSize: '14px', fontWeight: '500',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          {toast}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Business info */}
        <section style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px', marginBottom: '24px' }}>
          <h2 style={sectionTitleStyle}>Business Information</h2>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 300px' }}>
              <label style={labelStyle}>Business Name</label>
              <input type="text" name="name" value={form.name} onChange={handleChange} style={inputStyle} placeholder="Acme Healthcare Inc." />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>Sector</label>
              <select name="sector" value={form.sector} onChange={handleChange} style={inputStyle}>
                {SECTORS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows={4} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Describe your business and services..." />
          </div>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>Province</label>
              <select name="province" value={form.province} onChange={handleChange} style={inputStyle}>
                {PROVINCES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>City</label>
              <input type="text" name="city" value={form.city} onChange={handleChange} style={inputStyle} placeholder="Toronto" />
            </div>
          </div>
          <div style={{ marginBottom: '0' }}>
            <label style={labelStyle}>Logo URL (or upload placeholder)</label>
            <input type="text" name="logoUrl" value={form.logoUrl} onChange={handleChange} style={inputStyle} placeholder="https://example.com/logo.png" />
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
              Direct image upload coming soon. For now, enter the URL of your logo.
            </div>
          </div>
        </section>

        {/* Contact info */}
        <section style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px', marginBottom: '24px' }}>
          <h2 style={sectionTitleStyle}>Contact Information</h2>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>Phone</label>
              <input type="tel" name="phone" value={form.phone} onChange={handleChange} style={inputStyle} placeholder="(416) 555-1234" />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>Email</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} style={inputStyle} placeholder="contact@company.ca" />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>Website</label>
              <input type="url" name="website" value={form.website} onChange={handleChange} style={inputStyle} placeholder="https://company.ca" />
            </div>
          </div>
        </section>

        {/* Service areas */}
        <section style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px', marginBottom: '24px' }}>
          <h2 style={sectionTitleStyle}>Service Areas</h2>
          <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
            Select all provinces and territories where you provide services.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {PROVINCES.map((p) => {
              const selected = form.serviceAreas.includes(p.value);
              return (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => handleServiceAreaToggle(p.value)}
                  style={{
                    padding: '6px 14px', fontSize: '13px', fontWeight: '500', borderRadius: '8px',
                    border: selected ? '2px solid #3b82f6' : '1px solid #d1d5db',
                    backgroundColor: selected ? '#eff6ff' : '#fff',
                    color: selected ? '#1e40af' : '#475569',
                    cursor: 'pointer',
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Business hours */}
        <section style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px', marginBottom: '24px' }}>
          <h2 style={sectionTitleStyle}>Business Hours</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {DAYS.map((day) => {
              const hours = form.businessHours[day] || { open: '09:00', close: '17:00', closed: false };
              return (
                <div key={day} style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ width: '100px', fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>{day}</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#64748b', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={hours.closed}
                      onChange={() => handleHoursClosedToggle(day)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    Closed
                  </label>
                  {!hours.closed && (
                    <>
                      <input
                        type="time"
                        value={hours.open}
                        onChange={(e) => handleHoursChange(day, 'open', e.target.value)}
                        style={{ padding: '6px 8px', fontSize: '13px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                      />
                      <span style={{ color: '#94a3b8' }}>to</span>
                      <input
                        type="time"
                        value={hours.close}
                        onChange={(e) => handleHoursChange(day, 'close', e.target.value)}
                        style={{ padding: '6px 8px', fontSize: '13px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Social media */}
        <section style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px', marginBottom: '24px' }}>
          <h2 style={sectionTitleStyle}>Social Media Links</h2>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {[
              { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/...' },
              { key: 'twitter', label: 'Twitter / X', placeholder: 'https://twitter.com/...' },
              { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/...' },
              { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...' },
            ].map((social) => (
              <div key={social.key} style={{ flex: '1 1 240px' }}>
                <label style={labelStyle}>{social.label}</label>
                <input
                  type="url"
                  value={form.socialLinks[social.key] || ''}
                  onChange={(e) => handleSocialChange(social.key, e.target.value)}
                  style={inputStyle}
                  placeholder={social.placeholder}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Save button */}
        {saveMutation.isError && (
          <div style={{
            padding: '10px 14px', backgroundColor: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: '8px', fontSize: '13px', color: '#991b1b', marginBottom: '16px',
          }}>
            {saveMutation.error?.message || 'Failed to save profile'}
          </div>
        )}
        <button
          type="submit"
          disabled={saveMutation.isPending}
          style={{
            padding: '12px 32px', fontSize: '15px', fontWeight: '700', borderRadius: '10px',
            border: 'none',
            backgroundColor: saveMutation.isPending ? '#93c5fd' : '#3b82f6',
            color: '#fff', cursor: saveMutation.isPending ? 'wait' : 'pointer',
          }}
        >
          {saveMutation.isPending ? 'Saving...' : 'Save Profile'}
        </button>
      </form>
    </div>
  );
}

const sectionTitleStyle = {
  fontSize: '16px',
  fontWeight: '700',
  color: '#1e293b',
  marginBottom: '16px',
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '4px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '14px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  backgroundColor: '#fff',
  color: '#1e293b',
  outline: 'none',
  boxSizing: 'border-box',
};
